import { useState } from "react";
import { InlineWidget, PopupModal, useCalendlyEventListener } from "react-calendly";
import useAnalytics from "../../../hooks/useAnalytics";
import { IAdvisor, IMeeting, IProject, IScheduler, ISurveyLink } from "../../../types";
import Loading from "../../../components/Loading";
import { Link } from "react-router-dom";
import ClientProvider from "../../../modules/ClientProvider";
import { customAlphabet } from "nanoid";

export const MeetingLinks = ({ advisor, meeting, handleMeetingEvent, fetchMeetingDetails }: { advisor: IAdvisor, meeting: IMeeting, handleMeetingEvent: (e: any, meetingUuid: string) => void, fetchMeetingDetails: () => void }) => {
    const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
    const [isCancelOpen, setIsCancelOpen] = useState(false);
    const schedulerHttpClient = ClientProvider.provideSchedulerClient();

    const handleReschedule = (e: any, rescheduleUrl: string) => {
        e.preventDefault();
        setIsRescheduleOpen(true);
    }

    const handleCancel = (e: any, cancelUrl: string) => {
        e.preventDefault();
        setIsCancelOpen(true);
    }

    const handleCloseCancel = async (e: any, meetingUuid: string) => {
        setIsCancelOpen(false);
        await handleRefresh(e, meetingUuid);
    }

    const handleCloseReschedule = async (e: any, meetingUuid: string) => {
        setIsRescheduleOpen(false);
        await handleRefresh(e, meetingUuid);
    }

    const handleRefresh = async (e: any, meetingUuid: string) => {
        await schedulerHttpClient.refreshMeeting(meetingUuid);
        fetchMeetingDetails();
        handleMeetingEvent(e, meetingUuid);
    }

    if (meeting) {
        const invitee = meeting?.calendly_invitee_uri?.split("/invitees/")[1];
        const cancelUrl = `https://calendly.com/cancellations/${invitee}`;
        const rescheduleUrl = `https://calendly.com/reschedulings/${invitee}`;
        return (
            <div>
                <Link onClick={(e) => handleCancel(e, cancelUrl)} to={cancelUrl}>Cancel</Link> | <Link onClick={(e) => handleReschedule(e, rescheduleUrl)} to={rescheduleUrl}>Reschedule</Link>
                <PopupModal
                    url={`${rescheduleUrl}?hide_gdpr_banner=1&hide_gdpr_banner_close_button=1`}
                    onModalClose={(e) => handleCloseReschedule(e, meeting.uuid)}
                    prefill={{
                        name: advisor.user?.profile?.name || advisor.name,
                        email: advisor.user?.email_address || advisor.email_address,

                    }}
                    open={isRescheduleOpen}
                    rootElement={document.getElementById("root") as HTMLElement}
                />
                <PopupModal
                    url={`${cancelUrl}?hide_gdpr_banner=1&hide_gdpr_banner_close_button=1`}
                    onModalClose={(e) => handleCloseCancel(e, meeting.uuid)}
                    prefill={{
                        name: advisor.user?.profile?.name || advisor.name,
                        email: advisor.user?.email_address || advisor.email_address,
                    }}
                    open={isCancelOpen}
                    rootElement={document.getElementById("root") as HTMLElement}
                />
            </div>
        )
    } else {
        return (
            <div>
                Scheduled
            </div>
        )
    }
}

export const CalendlyScheduler = ({ project, scheduler, advisor, surveyLink, onMeetingCreated, name, email, inline = true, open = false, setOpen = () => { } }: { project: IProject, scheduler: IScheduler, advisor?: IAdvisor | null, surveyLink?: ISurveyLink | null, onMeetingCreated: (meeting: any) => void, name?: string, email?: string, inline?: boolean, open?: boolean, setOpen?: (open: boolean) => void }) => {

    const [inlineWidgetHeight, setInlineWidgetHeight] = useState<string>("0");
    const [widgetError, setWidgetError] = useState<string | null>(null);
    const { trackAdvisorEvent } = useAnalytics();   
    const schedulerHttpClient = ClientProvider.provideSchedulerClient();
    const generateAlphanumericId = customAlphabet('0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', 25);
    const [publicMeetingId, setPublicMeetingId] = useState<string>(generateAlphanumericId());
    
    useCalendlyEventListener({
        onEventScheduled: async (e) => {
            try {
                const { data, error } = await schedulerHttpClient.createPublicMeeting(project.tag, scheduler.uuid, { calendlyEventUri: e.data.payload.event.uri, calendlyInviteeUri: e.data.payload.invitee.uri }, { advisorPublicId: advisor?.public_id || null, surveyLinkPublicId: surveyLink?.public_id || null });
                if (data) {
                    if (data.meeting?.advisor) {
                        localStorage.setItem("eval" + surveyLink?.public_id, data.meeting.advisor.public_id);
                        trackAdvisorEvent(data.meeting.advisor, `Scheduled ${scheduler.type}`);
                    }
                    onMeetingCreated(data.meeting);
                    setPublicMeetingId(generateAlphanumericId());
                } else if (error) {
                    console.error('Error creating meeting:', error);
                    setWidgetError('Failed to create meeting. Please try again.');
                }
            } catch (err) {
                console.error('Error in event scheduled handler:', err);
                setWidgetError('Failed to create meeting. Please try again.');
            }
        },
        onPageHeightResize(e) {
            setInlineWidgetHeight(e.data.payload.height);
        },
        onProfilePageViewed: () => {
            // Reset error when user interacts with the widget
            setWidgetError(null);
        }
    });

    const handleClose = (e: any) => {
        setOpen(false);
    }

    // Validate scheduler before rendering
    if (!scheduler) {
        return <Loading />;
    }

    if (!scheduler.scheduling_url || scheduler.scheduling_url.trim() === '') {
        return (
            <div style={{ textAlign: 'center', padding: '20px', color: 'error.main' }}>
                <p>Calendar is not properly configured. Please contact support.</p>
            </div>
        );
    }

    if (widgetError) {
        return (
            <div style={{ textAlign: 'center', padding: '20px', color: 'error.main' }}>
                <p>{widgetError}</p>
                <button onClick={() => setWidgetError(null)} style={{ marginTop: '10px' }}>
                    Try Again
                </button>
            </div>
        );
    }

    if (inline) {
        return (
            <InlineWidget
                url={`${scheduler.scheduling_url}?hide_gdpr_banner=1&hide_gdpr_banner_close_button=1`}
                prefill={{
                    name: advisor?.user?.profile?.name || advisor?.name || name,
                    email: advisor?.user?.email_address || advisor?.email_address || email,
                    guests: [...(scheduler?.attendees?.map((attendee: any) => attendee.email) || []), ...(scheduler.internal_attendee ? scheduler.internal_attendee.split(",") : [])],
                    customAnswers: {
                        ...(advisor?.survey_link ? { "a1": `https://${advisor.survey_link.project.tag}.${process.env.REACT_APP_APP_ORIGIN}/meeting/${advisor.uuid}/${scheduler.type}/${publicMeetingId}` } : {})
                    }
                }}
                styles={{
                    height: inlineWidgetHeight,
                    width: '100%',
                    minWidth: '320px',
                    marginTop: '20px'
                }}
            />
        );
    } else {
        return (
            <PopupModal
                url={`${scheduler.scheduling_url}?hide_gdpr_banner=1&hide_gdpr_banner_close_button=1`}
                prefill={{
                    name: advisor?.user?.profile?.name || advisor?.name || name,
                    email: advisor?.user?.email_address || advisor?.email_address || email,
                    guests: [...(scheduler?.attendees?.map((attendee: any) => attendee.email) || []), ...(scheduler.internal_attendee ? scheduler.internal_attendee.split(",") : [])],
                    customAnswers: {
                        ...(advisor?.survey_link ? { "a1": `https://${advisor.survey_link.project.tag}.${process.env.REACT_APP_APP_ORIGIN}/meeting/${advisor.uuid}/${scheduler.type}/${publicMeetingId}` } : {})
                    }
                }}
                open={open}
                rootElement={document.getElementById("root") as HTMLElement}
                onModalClose={(e) => handleClose(e)}
            />
        );
    }
};